/**
 * Plain-text diagnostic report designed to be PASTED as is.
 *
 * It originated from a concrete case: the app stayed "syncing" forever because
 * the PowerSync service was not even running. From the screen there was no way
 * to distinguish "does not connect" from "connects but is slow", and describing
 * it by hand loses half the data.
 *
 * SINGLE SOURCE. The first version received its own object while the screen
 * rendered its rows separately: two sources for the same thing, so adding a
 * row to the UI left the report incomplete without anyone noticing. Now both
 * consume the SAME sections array: the screen renders it, this function
 * serializes it, and the hint is calculated by reading it.
 *
 * Each item has three fields for distinct reasons:
 *   · `label`/`value` — already translated text: what is displayed and copied.
 *   · `status`        — used only by the UI for color; the report ignores it.
 *   · `raw`           — UNTRANSLATED value, so logic does not depend on
 *                       language. Without it, the hint would stop firing as soon
 *                       as someone changed a translation.
 *
 * It does NOT include personal data: no email, name, or token. Whoever builds
 * the sections is responsible for truncating identifiers.
 */

export type DiagnosticStatus = "success" | "warning" | "danger" | "neutral";

export interface DiagnosticItem {
  /** Stable identifier, independent of language. Logic uses it. */
  key: string;
  /** Already translated label. */
  label: string;
  /** Already translated / formatted value, exactly as displayed. */
  value: string;
  /** UI only. */
  status?: DiagnosticStatus;
  /** Raw value for logic that must not depend on language. */
  raw?: string | number | boolean | null;
}

export interface DiagnosticSection {
  title: string;
  items: DiagnosticItem[];
}

export interface DiagnosticsReportInput {
  appName: string;
  /** ISO timestamp. It is passed in; the clock is not read here (pure function). */
  timestamp: string;
  sections: DiagnosticSection[];
}

/** Searches by `key`, never by label: labels are translated. */
function findItem(sections: DiagnosticSection[], key: string): DiagnosticItem | undefined {
  for (const s of sections) {
    const found = s.items.find((i) => i.key === key);
    if (found) {
      return found;
    }
  }
  return undefined;
}

/**
 * Actionable hint for the pattern that started all this: there is a session and
 * synchronization does not start. It is almost always a downed service, not the
 * app, and saying so here saves searching in the wrong place.
 */
function computeHint(sections: DiagnosticSection[]): string[] {
  const sessionItem = findItem(sections, "session");
  const sync = findItem(sections, "sync");
  if (
    sessionItem?.raw !== true ||
    !sync ||
    sync.raw === "connected" ||
    sync.raw === undefined ||
    sync.raw === null
  ) {
    return [];
  }
  return [
    "",
    "## Pista",
    `La sesión está activa pero PowerSync no llega a "connected" (${String(sync.raw)}).`,
    "Comprobar que el servicio de PowerSync responde en la URL de arriba: si da",
    "502 o no responde, el problema es la pila, no la app.",
  ];
}

export function buildDiagnosticsReport(input: DiagnosticsReportInput): string {
  const parts: string[] = [`# Diagnóstico — ${input.appName}`, input.timestamp];

  for (const section of input.sections) {
    parts.push("", `## ${section.title}`);
    for (const item of section.items) {
      // An empty field is MARKED rather than omitted: in a pasted report, missing
      // data and uncollected data read the same unless it says which one it is.
      parts.push(`- ${item.label}: ${item.value === "" ? "(sin dato)" : item.value}`);
    }
  }

  parts.push(...computeHint(input.sections));
  return parts.join("\n");
}
